import { Col, Row } from 'react-grid-system';
import styled from 'styled-components';
import { useRef, useCallback, useState } from 'react';
import { useErrorHandlerContext, useUserLanguage } from '@/customHooks';
import textsData from '@/textData/createOwnerTexts.json'
import { Button, RowSpacer } from '../atoms';
import { useNavigate } from 'react-router';
import { CreateOwnerMutation, UpdateOwnerMutation, useCreateOwnerMutation, UserGenderTypeOption, useUpdateOwnerMutation } from '@/graphql/generated';
import { FetchResult } from '@apollo/client';
import { ActivityType, OwnerDetail, AddressProps, UserPersonalDataProps, BillingDataProps, BankDataProps } from '@/custom-types';
import { AddressFormControlled } from './AddressFormControlled';
import { BillingFormControlled } from './BillingFormControlled';
import { BankFormControlled } from './BankFormControlled';
import { UserPersonalDataFormControlled } from './UserPersonalDataFormControlled';
import { WarnText } from '../atoms/WarnText';

type CreateOwnerFormProps = {
  ownerDetail: OwnerDetail | null
  activity: ActivityType
  readOnly?: boolean
  onSuccess?: (result: FetchResult<CreateOwnerMutation | UpdateOwnerMutation>) => void
}

type OwnerFormData = {
  // User personal data
  firstName: string | null,
  lastName: string | null,
  email: string | null,
  phoneNumber: string | null,
  phoneNumber2: string | null,
  documentId: string | null,
  nationality: string | null,
  birthDate: string | null,
  genderOption: UserGenderTypeOption | null,
  // Address
  street: string | null,
  buildingNumber: string | null,
  addressLine: string | null,
  city: string | null,
  state: string | null,
  country: string | null,
  zipCode: string | null,
  // Bank
  bankAccountName: string | null,
  bankAccountHolder: string | null,
  bankAccountEmail: string | null,
  bankAccountNumber: string | null,
  bankAccountPhone: string | null,
  // Billing
  billingLastName: string | null,
  billingFirstName: string | null,
  billingStreet: string | null,
  billingBuildingNumber: string | null,
  billingAddressLine: string | null,
  billingCity: string | null,
  billingCountry: string | null,
  billingState: string | null,
  billingZipCode: string | null,
  billingWereGoing: string | null,
  billingBic: string | null,
  billingObservation: string | null,
};

const emptyData: OwnerFormData = {
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  phoneNumber2: '',
  documentId: '',
  nationality: '',
  birthDate: '',
  genderOption: UserGenderTypeOption.Other,
  street: '',
  buildingNumber: '',
  addressLine: '',
  city: '',
  state: '',
  country: '',
  zipCode: '',
  bankAccountName: '',
  bankAccountHolder: '',
  bankAccountEmail: '',
  bankAccountNumber: '',
  bankAccountPhone: '',
  billingLastName: '',
  billingFirstName: '',
  billingStreet: '',
  billingBuildingNumber: '',
  billingAddressLine: '',
  billingCity: '',
  billingCountry: '',
  billingState: '',
  billingZipCode: '',
  billingWereGoing: '',
  billingBic: '',
  billingObservation: '',
};

const StyledForm = styled.form`
  background-color: ${({ theme }) => theme.colors.light};
  padding: 2rem;
`

const SectionWrapper = styled.div`
  border-radius: ${({ theme }) => theme.border.radius.base};
  border-style: solid;
  border-color: ${({ theme }) => theme.colors.border};
  border-width: 1px;
  padding: 2rem;
  margin-top: 1.5rem;
  box-sizing: border-box;
  position: relative;
  margin-bottom: 1.5rem;
  & > span {
    background-color: ${({ theme }) => theme.colors.light};
    position: absolute;
    padding-left: 1rem;
    padding-right: 1rem;
    top: -1.3rem;
    line-height: 0;
  }
`

export const CreateOwnerForm = ({ ownerDetail, activity, readOnly, onSuccess }: CreateOwnerFormProps) => {
  const userLanguage = useUserLanguage();
  const texts = userLanguage === 'es' ? textsData.es : textsData.en

  const ownerFormDataRef = useRef<OwnerFormData>(emptyData);

  const [isWarnTextHidden, setIsWarnTextHidden] = useState(true)

  const navigate = useNavigate();

  const { open: errorHandle } = useErrorHandlerContext();

  const [createOwner, { loading: createOwnerLoading }] = useCreateOwnerMutation();
  const [updateOwner, { loading: updateOwnerLoading }] = useUpdateOwnerMutation();

  const onCreateSuccessHandle = (response: FetchResult<CreateOwnerMutation>) => {
    const typeName = response.data?.createOwner.__typename

    if (typeName === 'OwnerOutput') onSuccess?.(response)
    else if (typeName === 'ActionResponseMessageOutput') setIsWarnTextHidden(false)
    
    return
  }

  const onUpdateSuccessHandle = (response: FetchResult<UpdateOwnerMutation>) => {
    onSuccess?.(response)
  }

  const onChangeUserDataHandle = useCallback((userData: UserPersonalDataProps) => {
    ownerFormDataRef.current.firstName = userData.firstName
    ownerFormDataRef.current.lastName = userData.lastName
    ownerFormDataRef.current.email = userData.email
    ownerFormDataRef.current.phoneNumber = userData.phoneNumber
    ownerFormDataRef.current.phoneNumber2 = userData.secondaryPhoneNumber
    ownerFormDataRef.current.documentId = userData.documentId
    ownerFormDataRef.current.nationality = userData.nationality
    ownerFormDataRef.current.birthDate = userData.birthDate
    ownerFormDataRef.current.genderOption = userData.genderOption
  }, [])

  const onChangeAddressHandle = useCallback((address: AddressProps) => {
    ownerFormDataRef.current.street = address.street
    ownerFormDataRef.current.buildingNumber = address.buildingNumber
    ownerFormDataRef.current.addressLine = address.addressLine
    ownerFormDataRef.current.country = address.country
    ownerFormDataRef.current.state = address.state
    ownerFormDataRef.current.city = address.city
    ownerFormDataRef.current.zipCode = address.zipCode
  }, [])

  const onChangeBankDataHandle = useCallback((bankData: BankDataProps) => {
    ownerFormDataRef.current.bankAccountName = bankData.name
    ownerFormDataRef.current.bankAccountHolder = bankData.accountHolderName
    ownerFormDataRef.current.bankAccountEmail = bankData.email
    ownerFormDataRef.current.bankAccountNumber = bankData.accountNumber
    ownerFormDataRef.current.bankAccountPhone = bankData.phone
  }, [])

  const onChangeBillingDataHandle = useCallback((billingData: BillingDataProps) => {
    ownerFormDataRef.current.billingStreet = billingData.street
    ownerFormDataRef.current.billingBuildingNumber = billingData.buildingNumber
    ownerFormDataRef.current.billingAddressLine = billingData.addressLine
    ownerFormDataRef.current.billingCountry = billingData.country
    ownerFormDataRef.current.billingState = billingData.state
    ownerFormDataRef.current.billingCity = billingData.city
    ownerFormDataRef.current.billingZipCode = billingData.zipCode
    ownerFormDataRef.current.billingFirstName = billingData.firstName
    ownerFormDataRef.current.billingLastName = billingData.lastName
    ownerFormDataRef.current.billingWereGoing = billingData.wereGoing
    ownerFormDataRef.current.billingBic = billingData.bic
    ownerFormDataRef.current.billingObservation = billingData.observation
  }, [])

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    switch (activity) {
      case 'creating':
        createOwner({
          variables: {
            firstName: ownerFormDataRef.current.firstName || '',
            lastName: ownerFormDataRef.current.lastName || '',
            email: ownerFormDataRef.current.email || '',
            phoneNumber: ownerFormDataRef.current.phoneNumber || '',
            phoneNumber2: ownerFormDataRef.current.phoneNumber2 || '',
            street: ownerFormDataRef.current.street || '',
            buildingNumber: ownerFormDataRef.current.buildingNumber || '',
            addressLine: ownerFormDataRef.current.addressLine || '',
            city: ownerFormDataRef.current.city || '',
            state: ownerFormDataRef.current.state || '',
            country: ownerFormDataRef.current.country || '',
            zipCode: ownerFormDataRef.current.zipCode || '',
            documentId: ownerFormDataRef.current.documentId || '',
            nationality: ownerFormDataRef.current.nationality || '',
            birthDate: ownerFormDataRef.current.birthDate || '',
            genderOption: ownerFormDataRef.current.genderOption || UserGenderTypeOption.Other,
            bankAccountName: ownerFormDataRef.current.bankAccountName || '',
            bankAccountHolder: ownerFormDataRef.current.bankAccountHolder || '',
            bankAccountEmail: ownerFormDataRef.current.bankAccountEmail || '',
            bankAccountNumber: ownerFormDataRef.current.bankAccountNumber || '',
            bankAccountPhone: ownerFormDataRef.current.bankAccountPhone || '',
            // Billing fields can be added here if needed by backend
          },
        })
          .then(onCreateSuccessHandle)
          .catch(errorHandle);
        break;

      case 'updating':
        updateOwner({
          variables: {
            id: ownerDetail?.user.id,
            firstName: ownerFormDataRef.current.firstName || undefined,
            lastName: ownerFormDataRef.current.lastName || undefined,
            email: ownerFormDataRef.current.email || undefined,
            phoneNumber: ownerFormDataRef.current.phoneNumber || undefined,
            phoneNumber2: ownerFormDataRef.current.phoneNumber2 || undefined,
            street: ownerFormDataRef.current.street || undefined,
            buildingNumber: ownerFormDataRef.current.buildingNumber || undefined,
            addressLine: ownerFormDataRef.current.addressLine || undefined,
            city: ownerFormDataRef.current.city || undefined,
            state: ownerFormDataRef.current.state || undefined,
            country: ownerFormDataRef.current.country || undefined,
            zipCode: ownerFormDataRef.current.zipCode || undefined,
            documentId: ownerFormDataRef.current.documentId || undefined,
            nationality: ownerFormDataRef.current.nationality || undefined,
            birthDate: ownerFormDataRef.current.birthDate || undefined,
            genderOption: ownerFormDataRef.current.genderOption || undefined,
            bankAccountName: ownerFormDataRef.current.bankAccountName || undefined,
            bankAccountHolder: ownerFormDataRef.current.bankAccountHolder || undefined,
            bankAccountEmail: ownerFormDataRef.current.bankAccountEmail || undefined,
            bankAccountNumber: ownerFormDataRef.current.bankAccountNumber || undefined,
            bankAccountPhone: ownerFormDataRef.current.bankAccountPhone || undefined,
            // Billing fields can be added here if needed by backend
          },
        })
          .then(onUpdateSuccessHandle)
          .catch(errorHandle);
        break;
    }
  };

  return (
    <StyledForm onSubmit={handleSubmit}>
      <SectionWrapper>
        <span><h2>{texts.user.title}</h2></span>
        <UserPersonalDataFormControlled
          readOnly={readOnly}
          onChange={onChangeUserDataHandle}
          defaultUserData={{
            firstName: ownerDetail?.firstName ?? '',
            lastName: ownerDetail?.lastName ?? '',
            email: ownerDetail?.user.email ?? '',
            phoneNumber: ownerDetail?.phoneNumber ?? '',
            secondaryPhoneNumber: ownerDetail?.phoneNumber2 ?? '',
            documentId: ownerDetail?.documentId ?? '',
            nationality: ownerDetail?.nationality ?? '',
            birthDate: ownerDetail?.birthDate ?? '',
            genderOption: ownerDetail?.genderOption ?? ''
          }}
        />
      </SectionWrapper>
      <br />
      <SectionWrapper>
        <span><h2>{texts.address.title}</h2></span>
        <AddressFormControlled
          readOnly={readOnly}
          onChange={onChangeAddressHandle}
          defaultAddress={{
            street: ownerDetail?.address.street ?? '',
            buildingNumber: ownerDetail?.address.buildingNumber ?? '',
            addressLine: ownerDetail?.address.addressLine ?? '',
            country: ownerDetail?.address.country?.iso2 ?? '',
            state: ownerDetail?.address.state?.geonamesAdmin1Code ?? '',
            city: ownerDetail?.address.city?.geonamesId ?? '',
            zipCode: ownerDetail?.address.zipCode ?? '',
          }}
        />
      </SectionWrapper>
      <br />
      <SectionWrapper>
        <span><h2>{texts.bankInfo.title}</h2></span>
        <BankFormControlled
          readOnly={readOnly}
          onChange={onChangeBankDataHandle}
          defaultData={{
            name: ownerDetail?.bankAccount?.name || '',
            accountHolderName: ownerDetail?.bankAccount?.holderName || '',
            accountNumber: ownerDetail?.bankAccount?.accountNumber || '',
            email: ownerDetail?.bankAccount?.email || '',
            phone: ownerDetail?.bankAccount?.phone || '',
          }}
        />
      </SectionWrapper>
      <br />
      <SectionWrapper>
        <span><h2>{texts.billing.title}</h2></span>
        <BillingFormControlled
          readOnly={readOnly}
          onChange={onChangeBillingDataHandle}
          defaultData={{
            street: '',
            buildingNumber: '',
            addressLine: '',
            country: '',
            state: '',
            city: '',
            zipCode: '',
            firstName: '',
            lastName: '',
            bic: '',
            wereGoing: '',
            observation: '',
          }}
        />
      </SectionWrapper>
      <RowSpacer height={1} />
      <WarnText hidden={isWarnTextHidden} text={texts.warnText} />
      <RowSpacer height={1} />
      <Row justify="center">
        <Col xs="content">
          <Button secondary onClick={() => navigate(-1)}>{texts.user.cancelButton}</Button>
        </Col>
        <Col xs="content">
          <Button submit isLoading={createOwnerLoading || updateOwnerLoading}>{texts.user.confirmButton}</Button>
        </Col>
      </Row>
    </StyledForm>
  );
};